/**
 * SOLID Review
 * S — Strong. One use case: believe the claim — the plan with the believed floor, the server's next round.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandBelieveAccepted, ICommandNothing, IPayloadBelieve } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/** The decider believes (FE1's believe): the next round opens at the believed claim's floor. */
export class BelieveUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandNothing, IPayloadBelieve, TAnswered<ICommandBelieveAccepted['answer']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planBelieve(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadBelieve) {
    return this.deps.rounds.believeClaim(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadBelieve, answered: TAnswered<ICommandBelieveAccepted['answer']>) {
    const accepted: ICommandBelieveAccepted = { request: plan, answer: answered.value };
    return this.deps.decisions.believeAccepted(ctx, input, accepted);
  }
}
